<template>
  <!-- PS-136. The homepage's one "what we cover" section - it replaced the old
       SmartestCover cards here, which showed personal injury, "public"
       liability and races as covered. Card wording matches Policy Document
       and IPID 11/26: check it against the current wording before editing,
       and do not add prices here. Accidental and malicious damage is only
       covered on the Theft and Accidental Damage cover type, so never list it
       as included. -->
  <section class="cover-section hp-block-container">
    <h2 class="hp-block-title">What our bicycle insurance covers</h2>
    <p class="cover-section__intro">
      Every policy covers the essentials. Add the extras that fit how you ride.
    </p>

    <p class="ps-label cover-section__group">Included with every policy</p>
    <ul class="cover-section__grid cover-section__grid--standard">
      <li
        v-for="card in standard"
        :key="card.title"
        class="cover-card cover-card--standard"
      >
        <div class="cover-card__top">
          <span class="cover-card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path
                v-for="(d, i) in card.icon"
                :key="i"
                :d="d"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span class="cover-card__badge cover-card__badge--included">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M5 12.5l4.5 4.5L19 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Included
          </span>
        </div>
        <h3 class="cover-card__title">{{ card.title }}</h3>
        <p class="cover-card__text">{{ card.text }}</p>
      </li>
    </ul>

    <p class="ps-label cover-section__group">Add if you need it</p>
    <ul class="cover-section__grid cover-section__grid--optional">
      <li
        v-for="card in optional"
        :key="card.title"
        class="cover-card cover-card--optional"
      >
        <div class="cover-card__top">
          <span class="cover-card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path
                v-for="(d, i) in card.icon"
                :key="i"
                :d="d"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span class="cover-card__badge cover-card__badge--optional">
            + Optional
          </span>
        </div>
        <h3 class="cover-card__title">{{ card.title }}</h3>
        <p class="cover-card__text">{{ card.text }}</p>
      </li>
    </ul>

    <p class="cover-section__note">
      Limits, exclusions, security requirements and a policy excess apply.
      Accidental and malicious damage is only covered with Theft and Accidental
      Damage.
      <nuxt-link no-prefetch to="/what-we-cover"
        >See everything we cover</nuxt-link
      >.
    </p>

    <div class="cover-section__footer">
      <nuxt-link
        no-prefetch
        to="/quote?src=home-cover"
        class="button ps-button cover-section__cta"
      >
        Get a quote
      </nuxt-link>
      <ul class="cover-section__links">
        <li v-for="link in links" :key="link.to">
          <nuxt-link no-prefetch :to="link.to">{{ link.label }}</nuxt-link>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
// Line icons on the site's 24px grid, drawn at a 1.7 stroke with round caps
// and joins like the quick-quote panel's. Theft, damage and family reuse that
// panel's paths; the rest are drawn to match.
const ICONS = {
  lock: [
    'M5.5 10.5h13a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z',
    'M8 10.5V7.5a4 4 0 0 1 8 0v3',
  ],
  wrench: [
    'M14.9 6.4a1 1 0 0 0 0 1.4l1.3 1.3a1 1 0 0 0 1.4 0l3.1-3.1a5.5 5.5 0 0 1-7.3 7.3l-5.9 5.9a2 2 0 0 1-2.8-2.8l5.9-5.9a5.5 5.5 0 0 1 7.3-7.3l-3 3z',
  ],
  flag: ['M5.5 21V4', 'M5.5 4.5h11l-2.2 4 2.2 4h-11'],
  renew: [
    'M19.5 12a7.5 7.5 0 0 1-13.3 4.8',
    'M4.5 12a7.5 7.5 0 0 1 13.3-4.8',
    'M18 3.8v3.6h-3.6',
    'M6 20.2v-3.6h3.6',
  ],
  bike: [
    'M6 18.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
    'M18 18.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
    'M6 15l3.2-6.5h6.3L18 15',
    'M9.2 8.5 12 15H6',
    'M7.8 6h3',
    'M15.5 8.5l-.9-2.5h2',
  ],
  bolt: ['M13 3.5 5.5 13.5h6l-1 7 7.5-10h-6l1-7z'],
  family: [
    'M9.5 11.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    'M3.5 19.5a6 6 0 0 1 12 0',
    'M16.5 11a2.5 2.5 0 1 0 0-5',
    'M17 13.5a4.5 4.5 0 0 1 3.5 4.4',
  ],
  shirt: [
    'M9 4.5 4.5 6.8l1.8 3.7 2-.9v9.9h7.4V9.6l2 .9 1.8-3.7L15 4.5a3 3 0 0 1-6 0z',
  ],
  heart: [
    'M12 20s-7-4.4-8.6-9A4.6 4.6 0 0 1 12 7.4 4.6 4.6 0 0 1 20.6 11C19 15.6 12 20 12 20z',
    'M12 10.5v5',
    'M9.5 13h5',
  ],
  shield: [
    'M12 3.5 19 6v5.5c0 4.2-2.9 7.4-7 9-4.1-1.6-7-4.8-7-9V6l7-2.5z',
    'M9 12l2.2 2.2L15.5 10',
  ],
  globe: [
    'M12 20.5a8.5 8.5 0 1 0 0-17 8.5 8.5 0 0 0 0 17z',
    'M3.5 12h17',
    'M12 3.5c2.3 2.4 3.4 5.2 3.4 8.5s-1.1 6.1-3.4 8.5c-2.3-2.4-3.4-5.2-3.4-8.5S9.7 5.9 12 3.5z',
  ],
  trophy: [
    'M8 4.5h8v4.8a4 4 0 0 1-8 0V4.5z',
    'M8 6.5H5.5a2.8 2.8 0 0 0 2.8 3.4',
    'M16 6.5h2.5a2.8 2.8 0 0 1-2.8 3.4',
    'M12 13.3V17',
    'M9 19.5h6',
  ],
}

export default {
  name: 'CoverSummary',
  data() {
    return {
      standard: [
        {
          title: 'New-for-old',
          text: 'Replacements are new bikes of the same make and model, or equivalent, with no deduction for wear and tear, up to your sum insured.',
          icon: ICONS.renew,
        },
        {
          title: 'Replacement bike hire',
          text: 'Up to £500 a year towards hiring a bike while yours is repaired or replaced after a claim.',
          icon: ICONS.bike,
        },
        {
          // Sportives aren't named in Policy Document 11/26: they're covered
          // only because they fall outside its definition of competition (an
          // event where a winner is selected), so make no stronger claim.
          title: 'Sportives and charity rides',
          text: 'Organised rides where no winner is selected. No add-on needed.',
          icon: ICONS.flag,
        },
        {
          title: 'Family riders',
          text: 'Your spouse or partner, parents, children, brothers and sisters who live with you permanently can ride your insured bikes.',
          icon: ICONS.family,
        },
        {
          title: 'Electric bikes',
          text: 'Road-legal e-bikes (EAPCs) are covered on the same terms. Twist-and-go e-bikes are excluded unless the throttle works in walk mode only.',
          icon: ICONS.bolt,
        },
      ],
      optional: [
        {
          title: 'Accessories and clothing',
          text: 'Fitted accessories, cycling clothing and hard-shell bike boxes, when stolen with your bike, or damaged with it on Theft and Accidental Damage.',
          icon: ICONS.shirt,
        },
        {
          title: 'Personal accident',
          text: 'Up to £50,000 if you’re injured riding, plus physio, dental and broken-bone benefits. You can add loss-of-earnings cover. Covers the named policyholder only.',
          icon: ICONS.heart,
        },
        {
          title: 'Personal liability',
          text: 'If you’re legally liable for injuring someone or damaging their property while cycling. Covers the named policyholder only, and not in the USA or Canada.',
          icon: ICONS.shield,
        },
        {
          title: 'Cover abroad',
          text: 'Standard cover is the UK, Channel Islands and Isle of Man. Add worldwide cover for up to 30 or 60 days per policy year.',
          icon: ICONS.globe,
        },
        {
          title: 'Competition and racing',
          text: 'Organised races and triathlons where a winner is selected.',
          icon: ICONS.trophy,
        },
      ],
      links: [
        { to: '/what-we-cover', label: 'See everything we cover' },
        { to: '/bike-theft-insurance', label: 'How our theft cover works' },
        { to: '/electric-bike-insurance', label: 'Electric bike insurance' },
        { to: '/bike-insurance', label: 'How bike insurance works' },
      ],
    }
  },
}
</script>

<style lang="scss" scoped>
// Contrast (PS-123): body copy $ps-body-color on white is ~8:1 and on the
// $ps-primary-50 tint ~6.8:1; titles are $ps-ink. Icons and badges use
// $ps-primary (~6.6:1 on white, ~5.7:1 on the tint); the Included badge is
// white on $ps-primary (~6.6:1).
.cover-section {
  border-top: 1px solid $ps-border;

  // The homepage container has no side padding on a phone.
  @include mobile {
    padding-left: 16px;
    padding-right: 16px;
  }
}

.cover-section__intro {
  max-width: 60ch;
  margin: -8px auto 36px;
  text-align: center;
  font-size: $ps-text-body;
  line-height: 1.6;
  color: $ps-body-color;

  @include tablet {
    margin-top: -20px;
  }
}

.cover-section__group {
  margin-bottom: 14px;
  color: $ps-body-color;

  & + .cover-section__grid {
    margin-bottom: 36px;
  }
}

.cover-section__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  // Two columns from 600px rather than Bulma's tablet mixin, which starts at
  // 769px and so would give a 768px iPad the single phone column.
  @media screen and (min-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

// Five included cards, so three columns like the optional row (3 + 2) rather
// than four with one left over.
.cover-section__grid--standard {
  @include desktop {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.cover-section__grid--optional {
  @include desktop {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.cover-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border-radius: $ps-radius-md;
}

.cover-card--standard {
  border: 1px solid $ps-primary-200;
  background: $ps-primary-50;
}

.cover-card--optional {
  border: 1px solid $ps-border;
  background: $ps-white;
}

.cover-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
}

.cover-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: $ps-primary;

  svg {
    width: 24px;
    height: 24px;
  }
}

.cover-card--standard .cover-card__icon {
  background: $ps-white;
}

.cover-card--optional .cover-card__icon {
  background: $ps-primary-50;
}

.cover-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: $ps-radius-pill;
  font-size: $ps-text-label;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
  }
}

.cover-card__badge--included {
  background: $ps-primary;
  color: $ps-white;
}

.cover-card__badge--optional {
  border: 1px solid $ps-primary;
  background: $ps-white;
  color: $ps-primary;
}

.cover-card__title {
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.3;
  color: $ps-ink;
}

.cover-card__text {
  font-size: $ps-text-small;
  line-height: 1.5;
  color: $ps-body-color;
}

.cover-section__note {
  margin-top: 20px;
  font-size: $ps-text-small;
  color: $ps-body-color;

  a {
    color: $ps-primary;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

// Button, then the guide links on one wrapping row. Spacing rather than dot
// separators: a separator ends up leading the next line whenever it wraps.
.cover-section__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 28px;
  margin-top: 24px;
}

.cover-section__links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;

  a {
    font-size: $ps-text-body;
    font-weight: 600;
    color: $ps-primary;
    text-decoration: underline;
    text-underline-offset: 3px;

    &:hover {
      color: $ps-primary-hover;
    }
  }
}
</style>
